import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';
import {
  type CompanyRegistrationOtpForm,
  companyRegistrationOtpSchema,
} from '@willnq/shared/validation/companyRegistrationSchemas';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';

import { OtpField } from '@/components/forms';
import { ROUTES } from '@/constants/routes';
import { AuthCard } from '@/features/identity/components/AuthCard';
import { AuthHeader } from '@/features/identity/components/AuthHeader';

import { RESEND_COOLDOWN_SECONDS, VERIFICATION_WINDOW_HOURS } from '../constants';
import { useCompanyRegistrationMutations } from '../hooks/useCompanyRegistrationMutations';

interface VerifyLocationState {
  registrationId?: string;
  email?: string;
}

/**
 * The corporate-email verification step of company registration (public). On success the
 * registration moves to PendingApproval — the applicant can sign in to the waiting screen while
 * the Platform Super Admin reviews. Still no Company exists.
 */
export function VerifyCompanyRegistrationPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = (location.state ?? {}) as VerifyLocationState;
  const registrationId = state.registrationId ?? searchParams.get('id') ?? '';
  const email = state.email ?? '';

  const { verifyRegistration, resendVerification, verifying } = useCompanyRegistrationMutations();
  const [serverError, setServerError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const { control, handleSubmit } = useForm<CompanyRegistrationOtpForm>({
    resolver: zodResolver(companyRegistrationOtpSchema),
    defaultValues: { otp: '' },
  });

  const onSubmit = handleSubmit(async ({ otp }) => {
    setServerError(null);
    const outcome = await verifyRegistration(registrationId, otp);
    if (outcome.ok) {
      setVerified(true);
      return;
    }
    setServerError(outcome.errors[0]?.message ?? 'Verification failed. Please try again.');
  });

  const resend = async () => {
    setServerError(null);
    await resendVerification({ registrationId });
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  };

  if (!registrationId) {
    return (
      <AuthCard>
        <AuthHeader
          title="Verification link is incomplete"
          subtitle="Start again from the company registration page."
        />
        <Button component={RouterLink} to={ROUTES.registerCompany} variant="contained" size="large">
          Register your company
        </Button>
      </AuthCard>
    );
  }

  if (verified) {
    return (
      <AuthCard>
        <AuthHeader
          title="Email verified"
          subtitle="Your registration is now awaiting review by the Willnq team. We'll email you once it's approved, and you can also sign in anytime to check its status."
        />
        <Button component={RouterLink} to={ROUTES.login} variant="contained" size="large">
          Sign in
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <AuthHeader
        title="Verify your corporate email"
        subtitle={
          email
            ? `Enter the 6-digit code we sent to ${email}.`
            : 'Enter the 6-digit code we sent to your corporate email.'
        }
      />
      <Stack component="form" onSubmit={(e) => void onSubmit(e)} spacing={2} noValidate>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}
        {/* Repeated here, not only on the form before it: this is the screen someone returns to hours
            later from an emailed link, and it is where the deadline actually starts to matter.
            Requesting a fresh code restarts the window, which is why the resend link below is the
            remedy rather than a nuisance. */}
        <Alert severity="warning" icon={false}>
          <Typography variant="body2">
            Verify within <strong>{VERIFICATION_WINDOW_HOURS} hours</strong> of your latest code, or
            this registration is deleted and the company name released. You&apos;d need to register
            again. Asking for a new code gives you another {VERIFICATION_WINDOW_HOURS} hours.
          </Typography>
        </Alert>
        <Stack spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Verification code
          </Typography>
          <OtpField name="otp" control={control} />
        </Stack>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={verifying}
          startIcon={verifying ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Verify email
        </Button>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Didn&apos;t get a code?{' '}
          {resendCooldown > 0 ? (
            <span>Resend in {resendCooldown}s</span>
          ) : (
            <Link component="button" type="button" onClick={() => void resend()}>
              Resend code
            </Link>
          )}
        </Typography>
      </Stack>
    </AuthCard>
  );
}
